import { getRuntimeConfig } from "@/app/config/runtime-config";
import { resolveActiveRealmContext, resolveRealmAuthBaseUrl } from "@/core/auth/realm";
import {
  WRITE_AUTHORITY_SCHEMA,
  isWriteAuthorityDomain,
  type DomainAuthority,
  type WriteAuthorityDomain,
} from "./write-authority-domains";

/** Schema `DomainAuthority`: `new` carries `flipped_at`, `legacy` never does. */
export type WriteAuthorityDomainEntry =
  | { authority: Extract<DomainAuthority, "legacy"> }
  | { authority: Extract<DomainAuthority, "new">; flippedAt: string };

/** Validated flat `GET /write-authority` body (contracts/write-authority-config.md). */
export interface WriteAuthorityDocument {
  schema: typeof WRITE_AUTHORITY_SCHEMA;
  updatedAt: string;
  domains: Partial<Record<WriteAuthorityDomain, WriteAuthorityDomainEntry>>;
  parameterVersion: number;
  stale: boolean;
}

export type WriteAuthorityUnavailableReason =
  | "http_status"
  | "not_json"
  | "malformed"
  | "unknown_schema"
  | "network"
  | "timeout";

/**
 * Every read settles to one of these — the client never throws. Anything
 * that is not a validated 200 is `unavailable`, which the provider evaluates
 * fail-closed (all domains read-only).
 */
export type WriteAuthorityReadResult =
  | { kind: "document"; document: WriteAuthorityDocument }
  | { kind: "unavailable"; reason: WriteAuthorityUnavailableReason; status?: number };

export interface WriteAuthorityClient {
  read(signal?: AbortSignal): Promise<WriteAuthorityReadResult>;
}

const WRITE_AUTHORITY_PATH = "/write-authority";

export class HttpWriteAuthorityClient implements WriteAuthorityClient {
  private readonly fetcher: typeof fetch;

  constructor(fetcher: typeof fetch = globalThis.fetch) {
    // Bound so native fetch is never invoked with the client as `this`
    // (same "Illegal invocation" hazard HttpAuthClient guards against).
    this.fetcher = fetcher === globalThis.fetch ? fetcher.bind(globalThis) : fetcher;
  }

  async read(signal?: AbortSignal): Promise<WriteAuthorityReadResult> {
    let response: Response;
    try {
      response = await this.fetcher(this.url(), {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
        // The service marks stale/503 bodies no-store and fresh bodies
        // max-age ≤ 30 s; the provider's own cadence is the freshness bound,
        // so a browser-cached fresh copy must never be served past it.
        cache: "no-store",
        signal,
      });
    } catch {
      return { kind: "unavailable", reason: "network" };
    }

    if (response.status !== 200) {
      return { kind: "unavailable", reason: "http_status", status: response.status };
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      return { kind: "unavailable", reason: "not_json", status: 200 };
    }

    return parseWriteAuthorityBody(body);
  }

  private url(): URL {
    // Resolved per read so construction is safe before the runtime config
    // is loaded; the root path sits outside the /auth and /admin prefixes.
    const baseUrl = resolveRealmAuthBaseUrl(resolveActiveRealmContext(getRuntimeConfig()));
    return new URL(WRITE_AUTHORITY_PATH, baseUrl);
  }
}

/**
 * Strict boundary parser for the flat response body (auth
 * `write-authority.schema.json`): timestamps are RFC 3339 date-times, a `new`
 * entry must carry `flipped_at`, a `legacy` entry must not. Unknown domain
 * keys are ignored (forward-compatible with new canonical domains); a known
 * key with a non-object/null entry or an unrecognised authority is malformed.
 */
export function parseWriteAuthorityBody(body: unknown): WriteAuthorityReadResult {
  if (!isRecord(body)) {
    return { kind: "unavailable", reason: "malformed", status: 200 };
  }
  if (body.schema !== WRITE_AUTHORITY_SCHEMA) {
    return { kind: "unavailable", reason: "unknown_schema", status: 200 };
  }
  if (
    !isRfc3339DateTime(body.updated_at) ||
    !isRecord(body.domains) ||
    !Number.isInteger(body.parameter_version) ||
    (body.parameter_version as number) < 1 ||
    typeof body.stale !== "boolean"
  ) {
    return { kind: "unavailable", reason: "malformed", status: 200 };
  }

  const domains: WriteAuthorityDocument["domains"] = {};
  for (const [key, entry] of Object.entries(body.domains)) {
    if (!isWriteAuthorityDomain(key)) {
      continue;
    }
    if (!isRecord(entry)) {
      return { kind: "unavailable", reason: "malformed", status: 200 };
    }
    if (entry.authority === "new" && isRfc3339DateTime(entry.flipped_at)) {
      domains[key] = { authority: "new", flippedAt: entry.flipped_at };
    } else if (entry.authority === "legacy" && entry.flipped_at === undefined) {
      domains[key] = { authority: "legacy" };
    } else {
      return { kind: "unavailable", reason: "malformed", status: 200 };
    }
  }

  return {
    kind: "document",
    document: {
      schema: WRITE_AUTHORITY_SCHEMA,
      updatedAt: body.updated_at,
      domains,
      parameterVersion: body.parameter_version as number,
      stale: body.stale,
    },
  };
}

// RFC 3339 `date-time` (JSON Schema `format: date-time`): full date, `T`,
// time with optional fraction, and a mandatory `Z` or numeric offset. §5.6
// makes the `T` and `Z` letters case-insensitive; the digits and colons are
// not. The value is preserved verbatim, never normalised.
const RFC3339_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[Tt](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:[Zz]|[+-](\d{2}):(\d{2}))$/;

/**
 * Shape AND calendar validity, checked component-wise: `Date.parse` would
 * normalise an impossible date (Feb 30 → Mar 2) and so accept it. RFC 3339
 * allows a leap second (`:60`).
 */
function isRfc3339DateTime(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const match = RFC3339_DATE_TIME.exec(value);
  if (match === null) {
    return false;
  }
  const [year, month, day, hour, minute, second] = match.slice(1, 7).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const offsetHour = match[7] === undefined ? 0 : Number(match[7]);
  const offsetMinute = match[8] === undefined ? 0 : Number(match[8]);
  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth(year, month) &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 60 &&
    offsetHour <= 23 &&
    offsetMinute <= 59
  );
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
